// PRODUCTION — simple collect-data step config forms
import React, { Fragment, useMemo } from 'react'
import { Formik, Form, useFormikContext } from 'formik'
import type { BasicSelectOption } from 'src/components/shared/form/BasicSelect'
import { mergeSelectOptionForCurrentValue } from 'src/selectors/collectStepReference'
import type { LmsTrainingPlain } from 'src/interfaces/collectStepReference'
import type {
  BaseStepConfig,
  BackgroundCheckStepConfig,
  BusinessEntityStepConfig,
  DotNumberStepConfig,
  DriverLicenseStepConfig,
  InsuranceStepConfig,
  LmsTrainingStepConfig,
  McNumberStepConfig,
  SignContractStepConfig,
  SsnAndAddressStepConfig,
  VehicleAndInsuranceStepConfig,
  VehicleRegistrationStepConfig,
} from 'src/interfaces/onboardingTemplate'
import { MedicalCardRequirement } from 'src/constants/collect-data/enums'
import { APPROVAL_TYPE_OPTIONS, POLICY_PRODUCT_OPTIONS } from './shared/constants'
import { AutoSave } from './shared/AutoSave'
import { ProtoCheckbox } from './shared/ProtoCheckbox'
import { SoloCheckbox } from './shared/SoloCheckbox'
import { ProtoNumberInput } from './shared/ProtoNumberInput'
import { ProtoRadioGroup } from './shared/ProtoRadioGroup'
import { ProtoSelect } from './shared/ProtoSelect'
import { ProtoTextField } from './shared/ProtoTextField'
import { ConfigRow } from './shared/layout'
import { RetroactiveCollectUnderOptionalFormik } from './shared/RetroactiveCollectRow'
import type { ConfigFormProps } from './shared/types'

const ignoreLockedCheckboxChange = () => {}

const MEDICAL_CARD_REQUIREMENT_OPTIONS = [
  { label: 'Required', value: MedicalCardRequirement.Required },
  { label: 'Optional', value: MedicalCardRequirement.Optional },
  { label: 'Not required', value: MedicalCardRequirement.Hidden },
]

export const DriverLicenseConfigForm: React.FC<ConfigFormProps<DriverLicenseStepConfig>> = ({
  initialValues,
  onSave,
  readOnly = false,
}) => (
  <Formik<DriverLicenseStepConfig>
    initialValues={initialValues}
    enableReinitialize
    onSubmit={onSave}
  >
    <Form>
      <AutoSave<DriverLicenseStepConfig> onSave={onSave} disabled={readOnly} />
      <ConfigRow label="Approval Type" disabled={readOnly}>
        <ProtoSelect<DriverLicenseStepConfig>
          name="approvalType"
          options={APPROVAL_TYPE_OPTIONS}
          disabled={readOnly}
        />
      </ConfigRow>
      <ConfigRow label="Require Expiration Date" disabled>
        <SoloCheckbox checked onChange={ignoreLockedCheckboxChange} disabled />
      </ConfigRow>
    </Form>
  </Formik>
)

// ─── 2. Business Entity ──────────────────────────────────────────────────────

export const BusinessEntityConfigForm: React.FC<ConfigFormProps<BusinessEntityStepConfig>> = ({
  initialValues,
  onSave,
  readOnly = false,
  retroactiveExistingContractors,
}) => (
  <Formik<BusinessEntityStepConfig>
    initialValues={initialValues}
    enableReinitialize
    onSubmit={onSave}
  >
    <Form>
      <AutoSave<BusinessEntityStepConfig> onSave={onSave} disabled={readOnly} />
      <ConfigRow label="Approval Type" disabled={readOnly}>
        <ProtoSelect<BusinessEntityStepConfig>
          name="approvalType"
          options={APPROVAL_TYPE_OPTIONS}
          disabled={readOnly}
        />
      </ConfigRow>
      <ConfigRow label="Optional" disabled={readOnly}>
        <ProtoCheckbox<BusinessEntityStepConfig> name="optional" disabled={readOnly} />
      </ConfigRow>
      {retroactiveExistingContractors !== null && retroactiveExistingContractors !== undefined && (
        <RetroactiveCollectUnderOptionalFormik<BusinessEntityStepConfig>
          readOnly={readOnly}
          checked={retroactiveExistingContractors.checked}
          onChange={retroactiveExistingContractors.onChange}
        />
      )}
      <ConfigRow label="Max Attempts" disabled={readOnly}>
        <ProtoNumberInput<BusinessEntityStepConfig>
          name="maxAttempts"
          min={0}
          disabled={readOnly}
        />
      </ConfigRow>
    </Form>
  </Formik>
)

// ─── 2b. DOT Number ──────────────────────────────────────────────────────────

export const DotNumberConfigForm: React.FC<ConfigFormProps<DotNumberStepConfig>> = ({
  initialValues,
  onSave,
  readOnly = false,
  retroactiveExistingContractors,
}) => (
  <Formik<DotNumberStepConfig> initialValues={initialValues} enableReinitialize onSubmit={onSave}>
    <Form>
      <AutoSave<DotNumberStepConfig> onSave={onSave} disabled={readOnly} />
      <ConfigRow label="Approval Type" disabled={readOnly}>
        <ProtoSelect<DotNumberStepConfig>
          name="approvalType"
          options={APPROVAL_TYPE_OPTIONS}
          disabled={readOnly}
        />
      </ConfigRow>
      <ConfigRow label="Medical card" disabled={readOnly}>
        <ProtoRadioGroup<DotNumberStepConfig>
          name="medicalCardRequirement"
          options={MEDICAL_CARD_REQUIREMENT_OPTIONS}
          disabled={readOnly}
        />
      </ConfigRow>
      <ConfigRow label="Optional" disabled={readOnly}>
        <ProtoCheckbox<DotNumberStepConfig> name="optional" disabled={readOnly} />
      </ConfigRow>
      {retroactiveExistingContractors !== null && retroactiveExistingContractors !== undefined && (
        <RetroactiveCollectUnderOptionalFormik<DotNumberStepConfig>
          readOnly={readOnly}
          checked={retroactiveExistingContractors.checked}
          onChange={retroactiveExistingContractors.onChange}
        />
      )}
    </Form>
  </Formik>
)

// ─── 2c. MC Number ───────────────────────────────────────────────────────────

export const McNumberConfigForm: React.FC<ConfigFormProps<McNumberStepConfig>> = ({
  initialValues,
  onSave,
  readOnly = false,
  retroactiveExistingContractors,
}) => (
  <Formik<McNumberStepConfig> initialValues={initialValues} enableReinitialize onSubmit={onSave}>
    <Form>
      <AutoSave<McNumberStepConfig> onSave={onSave} disabled={readOnly} />
      <ConfigRow label="Approval Type" disabled={readOnly}>
        <ProtoSelect<McNumberStepConfig>
          name="approvalType"
          options={APPROVAL_TYPE_OPTIONS}
          disabled={readOnly}
        />
      </ConfigRow>
      <ConfigRow label="Optional" disabled={readOnly}>
        <ProtoCheckbox<McNumberStepConfig> name="optional" disabled={readOnly} />
      </ConfigRow>
      {retroactiveExistingContractors !== null && retroactiveExistingContractors !== undefined && (
        <RetroactiveCollectUnderOptionalFormik<McNumberStepConfig>
          readOnly={readOnly}
          checked={retroactiveExistingContractors.checked}
          onChange={retroactiveExistingContractors.onChange}
        />
      )}
    </Form>
  </Formik>
)

// ─── 3. Background Check / MVR Upload ──────────────────────────────────────

function BackgroundCheckConfigFormFields({
  readOnly,
  yardstikPackageSelectOptions,
  retroactiveExistingContractors,
}: {
  readOnly: boolean
  yardstikPackageSelectOptions: BasicSelectOption[]
  retroactiveExistingContractors?: ConfigFormProps<BackgroundCheckStepConfig>['retroactiveExistingContractors']
}) {
  const { values } = useFormikContext<BackgroundCheckStepConfig>()
  const yardstikPackageDisabled = readOnly || !values.enableYardstik

  return (
    <Fragment>
      <ConfigRow label="Approval Type" disabled={readOnly}>
        <ProtoSelect<BackgroundCheckStepConfig>
          name="approvalType"
          options={APPROVAL_TYPE_OPTIONS}
          disabled={readOnly}
        />
      </ConfigRow>
      <ConfigRow label="Optional" disabled={readOnly}>
        <ProtoCheckbox<BackgroundCheckStepConfig> name="optional" disabled={readOnly} />
      </ConfigRow>
      {retroactiveExistingContractors !== null && retroactiveExistingContractors !== undefined && (
        <RetroactiveCollectUnderOptionalFormik<BackgroundCheckStepConfig>
          readOnly={readOnly}
          checked={retroactiveExistingContractors.checked}
          onChange={retroactiveExistingContractors.onChange}
        />
      )}
      <ConfigRow label="Enable Yardstik" disabled={readOnly}>
        <ProtoCheckbox<BackgroundCheckStepConfig> name="enableYardstik" disabled={readOnly} />
      </ConfigRow>
      <ConfigRow label="Yardstik Package" disabled={yardstikPackageDisabled}>
        <ProtoSelect<BackgroundCheckStepConfig>
          name="yardstikPackage"
          options={yardstikPackageSelectOptions}
          placeholder={
            yardstikPackageSelectOptions.length ? 'Select a package' : 'Loading packages…'
          }
          // Production serves ~250 packages (ENG-541). Opted in explicitly rather than left to
          // the length threshold so the field behaves the same on environments with a short list.
          searchable
          disabled={yardstikPackageDisabled}
        />
      </ConfigRow>
      <ConfigRow label="Continue On Incomplete" disabled={readOnly}>
        <ProtoCheckbox<BackgroundCheckStepConfig> name="continueOnIncomplete" disabled={readOnly} />
      </ConfigRow>
      <ConfigRow label="Manual Upload" disabled={readOnly}>
        <ProtoCheckbox<BackgroundCheckStepConfig> name="manualUpload" disabled={readOnly} />
      </ConfigRow>
      <ConfigRow label="Expires In Days" disabled={readOnly}>
        <ProtoNumberInput<BackgroundCheckStepConfig>
          name="expiresInDays"
          min={1}
          disabled={readOnly}
        />
      </ConfigRow>
    </Fragment>
  )
}

export const BackgroundCheckConfigForm: React.FC<ConfigFormProps<BackgroundCheckStepConfig>> = ({
  initialValues,
  onSave,
  yardstikPackageSelectOptions = [],
  readOnly = false,
  retroactiveExistingContractors,
}) => (
  <Formik<BackgroundCheckStepConfig>
    initialValues={initialValues}
    enableReinitialize
    onSubmit={onSave}
  >
    <Form>
      <AutoSave<BackgroundCheckStepConfig> onSave={onSave} disabled={readOnly} />
      <BackgroundCheckConfigFormFields
        readOnly={readOnly}
        yardstikPackageSelectOptions={yardstikPackageSelectOptions}
        retroactiveExistingContractors={retroactiveExistingContractors}
      />
    </Form>
  </Formik>
)

// ─── 4. SSN & Address ────────────────────────────────────────────────────────

export const SsnAndAddressConfigForm: React.FC<ConfigFormProps<SsnAndAddressStepConfig>> = ({
  initialValues,
  onSave,
  readOnly = false,
  retroactiveExistingContractors,
}) => (
  <Formik<SsnAndAddressStepConfig>
    initialValues={initialValues}
    enableReinitialize
    onSubmit={onSave}
  >
    <Form>
      <AutoSave<SsnAndAddressStepConfig> onSave={onSave} disabled={readOnly} />
      <ConfigRow label="Approval Type" disabled={readOnly}>
        <ProtoSelect<SsnAndAddressStepConfig>
          name="approvalType"
          options={APPROVAL_TYPE_OPTIONS}
          disabled={readOnly}
        />
      </ConfigRow>
      <ConfigRow label="Optional" disabled={readOnly}>
        <ProtoCheckbox<SsnAndAddressStepConfig> name="optional" disabled={readOnly} />
      </ConfigRow>
      {retroactiveExistingContractors !== null && retroactiveExistingContractors !== undefined && (
        <RetroactiveCollectUnderOptionalFormik<SsnAndAddressStepConfig>
          readOnly={readOnly}
          checked={retroactiveExistingContractors.checked}
          onChange={retroactiveExistingContractors.onChange}
        />
      )}
      <ConfigRow label="Require Proof of Work Auth" disabled={readOnly}>
        <ProtoCheckbox<SsnAndAddressStepConfig>
          name="requireProofOfWorkAuthorization"
          disabled={readOnly}
        />
      </ConfigRow>
      <ConfigRow label="Enforce SSN collection" disabled={readOnly}>
        <ProtoCheckbox<SsnAndAddressStepConfig> name="enforceSsnCollection" disabled={readOnly} />
      </ConfigRow>
    </Form>
  </Formik>
)

// ─── 5. Vehicle & Insurance ──────────────────────────────────────────────────

const VehicleAndInsuranceFields = ({ readOnly }: { readOnly: boolean }) => {
  const { values } = useFormikContext<VehicleAndInsuranceStepConfig>()
  const isInsuranceUploadDisabled = values.disableInsuranceUpload

  return (
    <Fragment>
      <ConfigRow label="Approval Type" disabled={readOnly}>
        <ProtoSelect<VehicleAndInsuranceStepConfig>
          name="approvalType"
          options={APPROVAL_TYPE_OPTIONS}
          disabled={readOnly}
        />
      </ConfigRow>
      <ConfigRow label="Disable Insurance Upload" disabled={readOnly}>
        <ProtoCheckbox<VehicleAndInsuranceStepConfig>
          name="disableInsuranceUpload"
          disabled={readOnly}
        />
      </ConfigRow>
      <ConfigRow label="Require Expiration Date" disabled={readOnly || !isInsuranceUploadDisabled}>
        {isInsuranceUploadDisabled ? (
          <ProtoCheckbox<VehicleAndInsuranceStepConfig>
            name="requireExpirationDate"
            disabled={readOnly}
          />
        ) : (
          <SoloCheckbox checked onChange={ignoreLockedCheckboxChange} disabled />
        )}
      </ConfigRow>
    </Fragment>
  )
}

export const VehicleAndInsuranceConfigForm: React.FC<
  ConfigFormProps<VehicleAndInsuranceStepConfig>
> = ({ initialValues, onSave, readOnly = false }) => (
  <Formik<VehicleAndInsuranceStepConfig>
    initialValues={initialValues}
    enableReinitialize
    onSubmit={onSave}
  >
    <Form>
      <AutoSave<VehicleAndInsuranceStepConfig> onSave={onSave} disabled={readOnly} />
      <VehicleAndInsuranceFields readOnly={readOnly} />
    </Form>
  </Formik>
)

export const VehicleRegistrationConfigForm: React.FC<
  ConfigFormProps<VehicleRegistrationStepConfig>
> = ({ initialValues, onSave, readOnly = false }) => (
  <Formik<VehicleRegistrationStepConfig>
    initialValues={initialValues}
    enableReinitialize
    onSubmit={onSave}
  >
    <Form>
      <AutoSave<VehicleRegistrationStepConfig> onSave={onSave} disabled={readOnly} />
      <ConfigRow label="Approval Type" disabled={readOnly}>
        <ProtoSelect<VehicleRegistrationStepConfig>
          name="approvalType"
          options={APPROVAL_TYPE_OPTIONS}
          disabled={readOnly}
        />
      </ConfigRow>
      <ConfigRow label="Require Expiration Date" disabled={readOnly}>
        <ProtoCheckbox<VehicleRegistrationStepConfig>
          name="requireExpirationDate"
          disabled={readOnly}
        />
      </ConfigRow>
    </Form>
  </Formik>
)

// ─── 5b. Sign contract & LMS (API metadata fields) ────────────────────────────

export const SignContractConfigForm: React.FC<ConfigFormProps<SignContractStepConfig>> = ({
  initialValues,
  onSave,
  docusignTemplateSelectOptions = [],
  readOnly = false,
}) => (
  <Formik<SignContractStepConfig>
    initialValues={initialValues}
    enableReinitialize
    onSubmit={onSave}
  >
    <Form>
      <AutoSave<SignContractStepConfig> onSave={onSave} disabled={readOnly} />
      <ConfigRow label="DocuSign template" required disabled={readOnly}>
        <ProtoSelect<SignContractStepConfig>
          name="docusignTemplateId"
          options={docusignTemplateSelectOptions}
          placeholder={
            docusignTemplateSelectOptions.length ? 'Select a template' : 'Loading templates…'
          }
          disabled={readOnly}
        />
      </ConfigRow>
      <ConfigRow label="Approval Type" disabled={readOnly}>
        <ProtoSelect<SignContractStepConfig>
          name="approvalType"
          options={APPROVAL_TYPE_OPTIONS}
          disabled={readOnly}
        />
      </ConfigRow>
    </Form>
  </Formik>
)

const LmsTrainingIdPick = ({
  options,
  trainings,
  readOnly,
}: {
  options: BasicSelectOption[]
  trainings: LmsTrainingPlain[]
  readOnly: boolean
}) => {
  const { setFieldValue } = useFormikContext<LmsTrainingStepConfig>()

  return (
    <ProtoSelect<LmsTrainingStepConfig>
      name="lmsTrainingId"
      options={options}
      placeholder={options.length ? 'Select a training' : 'Loading trainings…'}
      disabled={readOnly}
      onOptionSelect={(option) => {
        // The option label also carries externalId + description (candidate-facing pay notes);
        // the backend stores `lmsTrainingName` as the certificate name, so send only the title.
        const selectedTraining = trainings.find((training) => {
          return training.id === option.value
        })

        if (selectedTraining) {
          setFieldValue('lmsTrainingName', selectedTraining.title.trim())
        }
      }}
    />
  )
}

export const LmsTrainingConfigForm: React.FC<ConfigFormProps<LmsTrainingStepConfig>> = ({
  initialValues,
  onSave,
  lmsTrainingSelectOptions = [],
  lmsTrainings = [],
  readOnly = false,
}) => (
  <Formik<LmsTrainingStepConfig> initialValues={initialValues} enableReinitialize onSubmit={onSave}>
    <Form>
      <AutoSave<LmsTrainingStepConfig> onSave={onSave} disabled={readOnly} />
      <ConfigRow label="LMS training" required disabled={readOnly}>
        <LmsTrainingIdPick
          options={lmsTrainingSelectOptions}
          trainings={lmsTrainings}
          readOnly={readOnly}
        />
      </ConfigRow>
      <ConfigRow label="Expires in (days)" disabled={readOnly}>
        <ProtoNumberInput<LmsTrainingStepConfig> name="expiresInDays" min={1} disabled={readOnly} />
      </ConfigRow>
      <ConfigRow label="Allow manual upload" disabled={readOnly}>
        <ProtoCheckbox<LmsTrainingStepConfig> name="manualUpload" disabled={readOnly} />
      </ConfigRow>
      <ConfigRow label="Approval Type" disabled={readOnly}>
        <ProtoSelect<LmsTrainingStepConfig>
          name="approvalType"
          options={APPROVAL_TYPE_OPTIONS}
          disabled={readOnly}
        />
      </ConfigRow>
    </Form>
  </Formik>
)

// ─── 6. Insurance ────────────────────────────────────────────────────────────

const InsurancePolicyProductPick = () => {
  const { values } = useFormikContext<InsuranceStepConfig>()
  const merged = useMemo(
    () =>
      mergeSelectOptionForCurrentValue(
        POLICY_PRODUCT_OPTIONS,
        values.insurancePolicyProductId ?? undefined,
        'Product',
      ),
    [values.insurancePolicyProductId],
  )

  return (
    <ProtoSelect<InsuranceStepConfig>
      name="insurancePolicyProductId"
      options={merged}
      placeholder="Select a product"
      disabled
    />
  )
}

export const InsuranceConfigForm: React.FC<ConfigFormProps<InsuranceStepConfig>> = ({
  initialValues,
  onSave,
  readOnly = false,
}) => (
  <Formik<InsuranceStepConfig> initialValues={initialValues} enableReinitialize onSubmit={onSave}>
    <Form>
      <AutoSave<InsuranceStepConfig> onSave={onSave} disabled={readOnly} />
      <ConfigRow label="Policy Product" disabled>
        <InsurancePolicyProductPick />
      </ConfigRow>
      <ConfigRow label="Label" disabled>
        <ProtoTextField<InsuranceStepConfig> name="label" placeholder="Label" disabled />
      </ConfigRow>
      <ConfigRow label="Approval Type" disabled={readOnly}>
        <ProtoSelect<InsuranceStepConfig>
          name="approvalType"
          options={APPROVAL_TYPE_OPTIONS}
          disabled={readOnly}
        />
      </ConfigRow>
    </Form>
  </Formik>
)

// ─── 7. Generic (MVR, MC Number, DOT Number, etc.) ──────────────────────────

export const GenericStepConfigForm: React.FC<ConfigFormProps<BaseStepConfig>> = ({
  initialValues,
  onSave,
  readOnly = false,
}) => (
  <Formik<BaseStepConfig> initialValues={initialValues} enableReinitialize onSubmit={onSave}>
    <Form>
      <AutoSave<BaseStepConfig> onSave={onSave} disabled={readOnly} />
      <ConfigRow label="Approval Type" disabled={readOnly}>
        <ProtoSelect<BaseStepConfig>
          name="approvalType"
          options={APPROVAL_TYPE_OPTIONS}
          disabled={readOnly}
        />
      </ConfigRow>
    </Form>
  </Formik>
)
